import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Clock, AlertTriangle, PartyPopper, Loader2 } from 'lucide-react';
import { apiService } from '@/lib/api';
import DrawConfirmationModal from '@/components/DrawConfirmationModal';

/**
 * Public, no-login page reached from the email nudge (/confirm-draw/:token).
 * Owner taps one button in the email → lands here → confirms the deposit date.
 * Supports ?action=confirm|snooze|discard deep-links from the email buttons.
 */
export default function ConfirmDraw() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [draw, setDraw] = useState(null);
  const [state, setState] = useState('loading'); // loading | show | done | snoozed | discarded | error | already
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await apiService.getDrawByToken(token);
      setDraw(res.data);
      if (res.data?.status === 'deposited') setState('already');
      else setState('show');
    } catch (e) {
      setError(e?.response?.data?.detail || 'This link is invalid or has expired.');
      setState('error');
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const doConfirm = async ({ depositDate, itemPayments }) => {
    try { await apiService.confirmDrawByToken(token, { deposit_date: depositDate, item_payments: itemPayments }); setState('done'); }
    catch (e) { setError(e?.response?.data?.detail || 'Could not record the draw.'); setState('error'); }
  };
  const doSnooze = async () => {
    try { await apiService.snoozeDrawByToken(token); setState('snoozed'); }
    catch (e) { setError('Could not snooze.'); setState('error'); }
  };
  const doDiscard = async () => {
    try { await apiService.discardDrawByToken(token); setState('discarded'); }
    catch (e) { setError('Could not discard.'); setState('error'); }
  };

  // Handle email deep-link actions for snooze/discard automatically.
  useEffect(() => {
    if (state !== 'show') return;
    const action = params.get('action');
    if (action === 'snooze') doSnooze();
    else if (action === 'discard') doDiscard();
    // 'confirm' still shows the modal so the user can set the deposit date.
  }, [state]);

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-lg border border-slate-200 p-8 text-center" data-testid="confirm-draw-result">
        {children}
      </div>
    </div>
  );

  if (state === 'loading') {
    return <Shell><Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto" /><p className="text-slate-500 mt-3">Loading your draw…</p></Shell>;
  }
  if (state === 'error') {
    return <Shell><AlertTriangle className="w-10 h-10 text-red-500 mx-auto" /><h1 className="text-lg font-bold text-slate-800 mt-3">Something's off</h1><p className="text-slate-500 mt-1">{error}</p></Shell>;
  }
  if (state === 'already') {
    return <Shell><CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" /><h1 className="text-lg font-bold text-slate-800 mt-3">Already recorded</h1><p className="text-slate-500 mt-1">This draw was already confirmed as deposited. Nothing more to do.</p></Shell>;
  }
  if (state === 'done') {
    return <Shell><PartyPopper className="w-12 h-12 text-emerald-500 mx-auto" /><h1 className="text-xl font-bold text-slate-800 mt-3">Recorded! 🎯</h1><p className="text-slate-500 mt-1">Your draw is logged and interest is now accruing from the date you entered. You can close this tab.</p></Shell>;
  }
  if (state === 'snoozed') {
    return <Shell><Clock className="w-12 h-12 text-amber-500 mx-auto" /><h1 className="text-lg font-bold text-slate-800 mt-3">Okay — we'll remind you</h1><p className="text-slate-500 mt-1">Nothing was recorded. We'll nudge you again later. You can close this tab.</p></Shell>;
  }
  if (state === 'discarded') {
    return <Shell><AlertTriangle className="w-12 h-12 text-red-400 mx-auto" /><h1 className="text-lg font-bold text-slate-800 mt-3">Draw discarded</h1><p className="text-slate-500 mt-1">No draw was recorded and nothing changed on your loan. You can close this tab.</p></Shell>;
  }

  // state === 'show'
  return (
    <div className="min-h-screen bg-slate-50">
      <DrawConfirmationModal
        draw={draw}
        onConfirm={doConfirm}
        onSnooze={doSnooze}
        onDiscard={doDiscard}
      />
    </div>
  );
}
